// apps/web/src/components/forms/EmailPasswordFields.tsx
//
// Plan 04-12b Task 1 — shared form fragments for email + password + optional
// confirm-password.
//
// Used by SignUp, SignIn, ResetPassword. The component composes shadcn/ui's
// Form primitives (FormField + FormItem + FormLabel + FormControl + FormMessage)
// with react-hook-form's Controller. The shape is generic — the parent owns
// the form context (FormProvider via shadcn/ui's `Form`).

import type * as React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { Control, FieldValues, Path } from "react-hook-form";

export interface EmailFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  label?: string;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function EmailField<TFieldValues extends FieldValues>(
  props: EmailFieldProps<TFieldValues>,
): React.ReactElement {
  const {
    control,
    name,
    label = "Email",
    placeholder = "you@example.com",
    autoComplete = "email",
    autoFocus,
    disabled,
  } = props;
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="email"
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              disabled={disabled}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export interface PasswordFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  label?: string;
  autoComplete?: string;
  disabled?: boolean;
}

export function PasswordField<TFieldValues extends FieldValues>(
  props: PasswordFieldProps<TFieldValues>,
): React.ReactElement {
  const {
    control,
    name,
    label = "Password",
    autoComplete = "current-password",
    disabled,
  } = props;
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="password"
              autoComplete={autoComplete}
              disabled={disabled}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
